import { Controller, useFormContext } from "react-hook-form";
import React from "react";
import { Divider, Flex } from "@mantine/core";
import { IconCalendarWeek } from "@tabler/icons-react";
import dayjs from "dayjs";
import Hint from "@/shared/ui/hint";
import {
  NewOrderScenario,
  NewOrderType,
  TNewOrderForm,
} from "@/entities/new-order";
import styles from "@/widgets/create-order-form/ui/order-form.module.scss";
import { BasicOrderFormRowTitle } from "@/Pages/CreateCampaignPage/Styles/newOrdersBasics";
import { BasicRedAccentSpan } from "@/Theme/basicElements";
import { ApplicationSelector } from "@/features/application-selector";
import { DateInput } from "@/shared/ui/date-input";
import { IAppItem } from "@/Models/ApiModels";
import { getAppStore } from "@/shared/helpers";
import { throwNewToastError } from "@/Utils/MainUtils";
import { NewOrderTypeSelect } from "./new-order-type-selector";

export const NewOrderSelectors = () => {
  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<TNewOrderForm>();

  const { activeScenario, dateStop, dateStart, type } = watch();

  const onApplicationSelect = (app: IAppItem | null) => {
    if (app && type === NewOrderType.install_with_views) {
      const appStore = getAppStore(app.url);

      if (appStore === "google") {
        setValue("type", NewOrderType.install);
        throwNewToastError("Impressions are not available for google app");
      }
    }

    setValue("app", app);
  };

  return (
    <div className={styles.selectors}>
      <div className={styles.selector_row}>
        <div className={styles.row_label}>
          <BasicOrderFormRowTitle>
            Type <BasicRedAccentSpan>*</BasicRedAccentSpan>
          </BasicOrderFormRowTitle>
          <Hint popupText="Select the campaign type" />
        </div>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <NewOrderTypeSelect
              error={errors.type?.message}
              disabled={
                activeScenario === NewOrderScenario.extend ||
                activeScenario === NewOrderScenario.duplicate
              }
              {...field}
              value={field.value ?? ""}
            />
          )}
        />
      </div>
      <div className={styles.selector_row}>
        <div className={styles.row_label}>
          <BasicOrderFormRowTitle>
            Application <BasicRedAccentSpan>*</BasicRedAccentSpan>
          </BasicOrderFormRowTitle>
          <Hint popupText="Select an application" />
        </div>
        <Controller
          control={control}
          name="app"
          render={({ field }) => (
            <ApplicationSelector
              width={418}
              placeholder="Application"
              {...field}
              value={field.value}
              onChange={onApplicationSelect}
              disabled={
                activeScenario === "promote" ||
                activeScenario === "duplicate" ||
                activeScenario === "extend"
              }
            />
          )}
        />
      </div>
      <div className={styles.selector_row}>
        <div className={styles.row_label}>
          <BasicOrderFormRowTitle>
            Campaign period <BasicRedAccentSpan>*</BasicRedAccentSpan>
          </BasicOrderFormRowTitle>
          <Hint popupText="Set the date or date range of the campaign" />
        </div>
        <Flex gap={16} w={418} align="center">
          <Controller
            control={control}
            name="dateStart"
            render={({ field }) => (
              <DateInput
                flex={1}
                placeholder="Start date"
                leftSection={<IconCalendarWeek />}
                clearable
                minDate={dayjs().utc().toISOString()}
                maxDate={
                  dateStop ? dayjs(dateStop).utc().toISOString() : undefined
                }
                disabled={activeScenario === NewOrderScenario.extend}
                {...field}
              />
            )}
          />
          <Divider w={16} h={1} />
          <Controller
            control={control}
            name="dateStop"
            render={({ field }) => (
              <DateInput
                flex={1}
                placeholder="End date"
                leftSection={<IconCalendarWeek />}
                minDate={
                  dateStart
                    ? dayjs(dateStart).utc().toISOString()
                    : dayjs().utc().toISOString()
                }
                clearable
                disabled={activeScenario === NewOrderScenario.extend}
                {...field}
              />
            )}
          />
        </Flex>
      </div>
    </div>
  );
};
